import { useState, useEffect } from 'react'
import { X, Plus, GripVertical, Edit2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/lib/auth'
import { 
  getUserLoyaltyMemberships, 
  saveLoyaltyMembership,
  deleteLoyaltyMembership 
} from '@/lib/profile'
import type { UserLoyaltyMembership } from '@/types/profile'
import { LoyaltyProgramForm } from './loyalty-program-form'

interface LoyaltyProgramPillProps {
  membership: UserLoyaltyMembership
  isDragging?: boolean
  onEdit: () => void
  onDelete: () => void
  dragHandleProps?: any
}

function LoyaltyProgramPill({ 
  membership, 
  isDragging, 
  onEdit, 
  onDelete,
  dragHandleProps 
}: LoyaltyProgramPillProps) {
  const [isHovered, setIsHovered] = useState(false)
  
  return (
    <div
      className={cn(
        "group relative inline-flex items-center gap-2 px-6 py-3 rounded-full",
        "bg-[#E8E8FF] text-[#5B6FED] dark:bg-blue-900/30 dark:text-blue-300 font-medium text-sm",
        "transition-all duration-200 cursor-move select-none",
        "hover:bg-[#D9D9FF] dark:hover:bg-blue-900/50",
        membership.is_primary && "bg-[#5B6FED] text-white hover:bg-[#4A5FDD] dark:bg-blue-600 dark:hover:bg-blue-700",
        isDragging && "opacity-50 scale-95"
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      {...dragHandleProps}
    >
      <GripVertical 
        className={cn(
          "h-4 w-4 opacity-0 -ml-2 transition-opacity",
          "group-hover:opacity-50"
        )} 
      />
      
      <span className="font-medium">
        {membership.program?.program_name || 'Unknown Program'}
      </span>
      
      {membership.tier && (
        <span className={cn(
          "text-xs px-2 py-0.5 rounded-full",
          membership.is_primary 
            ? "bg-white/20 text-white/90" 
            : "bg-[#5B6FED]/10 text-[#5B6FED]/80"
        )}>
          {membership.tier}
        </span>
      )}
      
      {isHovered && (
        <div className="flex items-center gap-1 ml-2">
          <button
            onClick={(e) => {
              e.stopPropagation()
              onEdit()
            }}
            className={cn(
              "p-1 rounded-full transition-colors",
              membership.is_primary 
                ? "hover:bg-white/20" 
                : "hover:bg-[#5B6FED]/10"
            )}
            aria-label="Edit program"
          >
            <Edit2 className="h-3 w-3" />
          </button>
          
          <button
            onClick={(e) => {
              e.stopPropagation()
              onDelete()
            }}
            className={cn(
              "p-1 rounded-full transition-colors",
              membership.is_primary 
                ? "hover:bg-white/20 text-white/90" 
                : "hover:bg-red-500/10 text-red-500"
            )}
            aria-label="Remove program"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      )}
    </div>
  )
}

interface AddProgramPillProps {
  onClick: () => void
}

function AddProgramPill({ onClick }: AddProgramPillProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-2 px-6 py-3 rounded-full",
        "border-2 border-dashed border-[#5B6FED]/30 dark:border-blue-500/30",
        "text-[#5B6FED] dark:text-blue-400 font-medium text-sm",
        "transition-all duration-200",
        "hover:border-[#5B6FED]/50 hover:bg-[#E8E8FF]/50 dark:hover:border-blue-500/50 dark:hover:bg-blue-900/30"
      )}
    >
      <Plus className="h-4 w-4" />
      <span>Add Program</span>
    </button>
  )
}

interface LoyaltyProgramsModernProps {
  className?: string
}

export function LoyaltyProgramsModern({ className }: LoyaltyProgramsModernProps) {
  const { user } = useAuth()
  const [memberships, setMemberships] = useState<UserLoyaltyMembership[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingMembership, setEditingMembership] = useState<UserLoyaltyMembership | null>(null)
  const [draggedItem, setDraggedItem] = useState<UserLoyaltyMembership | null>(null)
  const [draggedOverItem, setDraggedOverItem] = useState<UserLoyaltyMembership | null>(null)
  
  useEffect(() => {
    if (user?.id) {
      loadMemberships()
    }
  }, [user?.id])
  
  async function loadMemberships() {
    if (!user?.id) return
    
    setIsLoading(true)
    try {
      const data = await getUserLoyaltyMemberships(user.id)
      setMemberships(data.sort((a, b) => (a.priority || 0) - (b.priority || 0)))
    } catch (error) {
      console.error('Failed to load loyalty memberships:', error)
    } finally {
      setIsLoading(false)
    }
  }
  
  const handleDelete = async (membership: UserLoyaltyMembership) => {
    if (!membership.id) return
    
    try {
      await deleteLoyaltyMembership(membership.id)
      await loadMemberships()
    } catch (error) {
      console.error('Failed to delete membership:', error)
    }
  }
  
  const handleDragStart = (e: React.DragEvent, membership: UserLoyaltyMembership) => {
    setDraggedItem(membership)
    e.dataTransfer.effectAllowed = 'move'
  }
  
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }
  
  const handleDragEnter = (membership: UserLoyaltyMembership) => {
    if (draggedItem && draggedItem.id !== membership.id) {
      setDraggedOverItem(membership)
    }
  }
  
  const handleDragLeave = () => {
    setDraggedOverItem(null)
  }
  
  const handleDrop = async (e: React.DragEvent, targetMembership: UserLoyaltyMembership) => {
    e.preventDefault()
    
    if (!draggedItem || draggedItem.id === targetMembership.id) {
      return
    }
    
    const newMemberships = [...memberships]
    const draggedIndex = newMemberships.findIndex(m => m.id === draggedItem.id)
    const targetIndex = newMemberships.findIndex(m => m.id === targetMembership.id)
    
    // Remove dragged item and insert at target position
    const [removed] = newMemberships.splice(draggedIndex, 1)
    newMemberships.splice(targetIndex, 0, removed)
    
    // Update priorities
    const updates = newMemberships.map((m, index) => ({
      ...m,
      priority: index + 1
    }))
    
    setMemberships(updates)
    
    // Save updated priorities to backend
    try {
      await Promise.all(
        updates.map(m => 
          saveLoyaltyMembership({ ...m, priority: m.priority })
        )
      )
    } catch (error) {
      console.error('Failed to update priorities:', error)
      // Reload to restore original state on error
      await loadMemberships()
    }
    
    setDraggedItem(null)
    setDraggedOverItem(null)
  }
  
  const handleDragEnd = () => {
    setDraggedItem(null)
    setDraggedOverItem(null)
  }
  
  // Group memberships by type
  const groupedMemberships = memberships.reduce((acc, membership) => {
    const type = membership.program?.program_type || 'other'
    if (!acc[type]) {
      acc[type] = []
    }
    acc[type].push(membership)
    return acc
  }, {} as Record<string, UserLoyaltyMembership[]>)
  
  const typeLabels: Record<string, string> = {
    airline: 'Airlines',
    hotel: 'Hotels',
    car_rental: 'Car Rentals',
    credit_card: 'Credit Cards',
    other: 'Other'
  }
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-[#5B6FED] dark:text-blue-400 text-sm">Loading programs...</div>
      </div>
    )
  }
  
  return (
    <div className={cn("space-y-8", className)}>
      {Object.entries(groupedMemberships).map(([type, typeMemberships]) => (
        <div key={type} className="space-y-4">
          <h3 className="text-sm font-semibold text-gray-600 dark:text-zinc-200 uppercase tracking-wide">
            {typeLabels[type] || type}
          </h3>
          
          <div className="flex flex-wrap gap-3">
            {typeMemberships.map((membership) => (
              <div
                key={membership.id}
                draggable
                onDragStart={(e) => handleDragStart(e, membership)}
                onDragOver={handleDragOver}
                onDragEnter={() => handleDragEnter(membership)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, membership)}
                onDragEnd={handleDragEnd}
                className={cn(
                  "relative",
                  draggedOverItem?.id === membership.id && "opacity-50"
                )}
              >
                {draggedOverItem?.id === membership.id && (
                  <div className="absolute inset-0 border-2 border-dashed border-[#5B6FED] dark:border-blue-400 rounded-full" />
                )}
                <LoyaltyProgramPill
                  membership={membership}
                  isDragging={draggedItem?.id === membership.id}
                  onEdit={() => setEditingMembership(membership)}
                  onDelete={() => handleDelete(membership)}
                />
              </div>
            ))}
            
            {typeMemberships.length === 0 && (
              <AddProgramPill onClick={() => setShowAddForm(true)} />
            )}
          </div>
        </div>
      ))}
      
      {memberships.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 space-y-4">
          <p className="text-gray-500 dark:text-zinc-400 text-sm">No loyalty programs added yet</p>
          <AddProgramPill onClick={() => setShowAddForm(true)} />
        </div>
      )}
      
      {memberships.length > 0 && (
        <div className="flex justify-center pt-4">
          <AddProgramPill onClick={() => setShowAddForm(true)} />
        </div>
      )}
      
      {/* Add/Edit Form Modal */}
      {(showAddForm || editingMembership) && user?.id && (
        <LoyaltyProgramForm
          userId={user.id}
          membership={editingMembership}
          onSave={() => {
            setShowAddForm(false)
            setEditingMembership(null)
            loadMemberships()
          }}
          onCancel={() => {
            setShowAddForm(false)
            setEditingMembership(null)
          }}
        />
      )}
    </div>
  )
}